import { useEffect, useState } from "react";
import { Loader2, X, ShieldAlert, Plus, CalendarPlus, Ban, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminBookingModal({ onClose, onCreated }) {
  const [mode, setMode] = useState("session"); // 'session' | 'block'
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [defaultBuffer, setDefaultBuffer] = useState(30);
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    service_slug: "",
    location_slug: "",
    date: today,
    start_time: "10:00",
    duration_min: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    notes: "",
    override_schedule: false,
    buffer_min: "",
    // block-specific
    label: "",
    block_duration_min: 60,
    whole_day: false,
    // recurrence (shared)
    recur_frequency: "none",
    recur_occurrences: 8,
  });

  useEffect(() => {
    Promise.all([
      api.get("/services"),
      api.get("/locations"),
      api.get("/admin/clients").catch(() => ({ data: [] })),
      api.get("/content").catch(() => ({ data: {} })),
    ]).then(([s, l, c, ct]) => {
      setServices(s.data);
      setLocations(l.data);
      setClients(c.data || []);
      const cfg = parseInt(ct.data?.["booking.default_buffer_min"] ?? "30", 10);
      if (!Number.isNaN(cfg)) setDefaultBuffer(cfg);
      setForm((f) => ({
        ...f,
        service_slug: s.data[0]?.slug || "",
        duration_min: s.data[0]?.duration_min || "",
        location_slug: l.data[0]?.slug || "",
        buffer_min: String(cfg ?? 30),
      }));
    });
  }, []);

  const pickClient = (email) => {
    if (!email) return;
    const c = clients.find((x) => x.email === email);
    if (c) {
      setForm((f) => ({ ...f, client_email: c.email, client_name: c.name || "", client_phone: c.phone || "" }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const recurrence = {
      frequency: form.recur_frequency,
      occurrences: form.recur_frequency === "none" ? 1 : Math.max(1, Math.min(26, parseInt(form.recur_occurrences, 10) || 1)),
    };
    try {
      if (mode === "block") {
        const payload = {
          location_slug: "",
          all_locations: true,
          date: form.date,
          start_time: form.whole_day ? "00:00" : form.start_time,
          duration_min: form.whole_day ? 1440 : (parseInt(form.block_duration_min, 10) || 60),
          label: form.label || (form.whole_day ? "Day off" : "Blocked"),
          override_schedule: true,
          recurrence,
        };
        const r = await api.post("/admin/bookings/block", payload);
        const n = r.data?.blocks_created || 1;
        const skipped = (r.data?.skipped || []).length;
        const series = recurrence.frequency !== "none";
        const base = series
          ? `${recurrence.occurrences} ${recurrence.frequency} blocks created`
          : (n > 1 ? "Time blocked across your whole schedule" : "Time blocked");
        toast.success(skipped ? `${base} (${skipped} skipped due to overlap)` : base);
      } else {
        const payload = {
          service_slug: form.service_slug,
          location_slug: form.location_slug,
          date: form.date,
          start_time: form.start_time,
          duration_min: form.duration_min ? parseInt(form.duration_min, 10) : undefined,
          client_name: form.client_name,
          client_email: form.client_email,
          client_phone: form.client_phone,
          notes: form.notes,
          override_schedule: form.override_schedule,
          buffer_min: form.buffer_min === "" ? undefined : parseInt(form.buffer_min, 10),
          recurrence,
        };
        const r = await api.post("/admin/bookings", payload);
        const series = r.data?._series_summary;
        if (series && series.created > 1) {
          const skipped = (series.skipped || []).length;
          toast.success(skipped ? `${series.created} ${recurrence.frequency} sessions booked (${skipped} skipped due to overlap)` : `${series.created} ${recurrence.frequency} sessions booked`);
        } else {
          toast.success("Booking created");
        }
      }
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
  const labelCls = "text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block";

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-booking-modal"
        className="bg-cream-soft border border-sand/70 rounded-3xl p-7 w-full max-w-2xl mt-8 mb-8 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-ink-soft">New entry</div>
            <h3 className="font-serif text-2xl text-ink">
              {mode === "block" ? "Block out time." : "Book a session manually."}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>

        {/* Mode toggle */}
        <div className="inline-flex rounded-full bg-cream border border-sand/80 p-1 mb-5" data-testid="booking-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("session")}
            data-testid="mode-session"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${
              mode === "session" ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"
            }`}
          >
            <CalendarPlus size={12} /> Session
          </button>
          <button
            type="button"
            onClick={() => setMode("block")}
            data-testid="mode-block"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-all ${
              mode === "block" ? "bg-navy text-cream" : "text-ink-soft hover:text-ink"
            }`}
          >
            <Ban size={12} /> Block time
          </button>
        </div>

        {mode === "block" ? (
          // ----------- BLOCK MODE -----------
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Label</label>
              <input
                className={inputCls}
                placeholder="e.g. Lunch, Day off, Training, Annual leave"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="block-label"
              />
            </div>

            {/* Personal block — always applies to Jesse's whole schedule. */}
            <div className="sm:col-span-2">
              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-navy/8 border border-navy/20" data-testid="block-scope-info">
                <Ban size={16} className="mt-0.5 text-navy flex-shrink-0" />
                <div>
                  <div className="text-sm text-ink font-medium">This blocks your whole schedule.</div>
                  <div className="text-xs text-ink-muted mt-0.5">
                    Since you're the only counsellor, no location will accept bookings during this time.
                  </div>
                </div>
              </div>
            </div>

            {/* Whole-day shortcut */}
            <div className="sm:col-span-2">
              <label className="flex items-start gap-3 p-3.5 rounded-xl bg-cream border border-sand/70 cursor-pointer hover:border-navy/40 transition-colors">
                <input
                  type="checkbox"
                  checked={form.whole_day}
                  onChange={(e) => setForm({ ...form, whole_day: e.target.checked })}
                  data-testid="block-whole-day"
                  className="mt-0.5 h-4 w-4 accent-navy"
                />
                <div>
                  <div className="text-sm text-ink font-medium">Block the whole day</div>
                  <div className="text-xs text-ink-soft mt-0.5">
                    Forces start time to 00:00 and duration to 24 hours.
                  </div>
                </div>
              </label>
            </div>

            <div>
              <label className={labelCls}>Date</label>
              <input className={inputCls} type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="block-date" />
            </div>
            <div>
              <label className={labelCls}>Start time</label>
              <input
                className={`${inputCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                type="time"
                step="900"
                required
                disabled={form.whole_day}
                value={form.whole_day ? "00:00" : form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                data-testid="block-start-time"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Duration (min)</label>
              <input
                className={`${inputCls} disabled:opacity-50 disabled:cursor-not-allowed`}
                type="number"
                min="5"
                max="1440"
                step="5"
                required
                disabled={form.whole_day}
                value={form.whole_day ? 1440 : form.block_duration_min}
                onChange={(e) => setForm({ ...form, block_duration_min: e.target.value })}
                data-testid="block-duration"
              />
            </div>

            <RepeatsField form={form} setForm={setForm} mode="block" />

            <div className="sm:col-span-2 text-xs text-ink-soft bg-cream border border-sand/60 rounded-xl px-4 py-3">
              Blocks always bypass working-hours rules — you can block any day, any time. They prevent both public and admin bookings from overlapping the slot.
            </div>
          </div>
        ) : (
          // ----------- SESSION MODE -----------
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Existing client (optional)</label>
              <select
                className={inputCls}
                value=""
                onChange={(e) => pickClient(e.target.value)}
                data-testid="admin-booking-client-picker"
              >
                <option value="">— pick a client to autofill —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.email}>{c.name || c.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Client name</label>
              <input className={inputCls} required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="admin-booking-client-name" />
            </div>
            <div>
              <label className={labelCls}>Client email</label>
              <input className={inputCls} type="email" required value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} data-testid="admin-booking-client-email" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Client phone</label>
              <input className={inputCls} value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
            </div>

            <div>
              <label className={labelCls}>Service</label>
              <select
                className={inputCls}
                value={form.service_slug}
                onChange={(e) => {
                  const s = services.find((x) => x.slug === e.target.value);
                  setForm({ ...form, service_slug: e.target.value, duration_min: s ? s.duration_min : form.duration_min });
                }}
                data-testid="admin-booking-service"
              >
                {services.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Location</label>
              <select className={inputCls} value={form.location_slug} onChange={(e) => setForm({ ...form, location_slug: e.target.value })} data-testid="admin-booking-location">
                {locations.map((l) => (
                  <option key={l.slug} value={l.slug}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Date</label>
              <input className={inputCls} type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="admin-booking-date" />
            </div>
            <div>
              <label className={labelCls}>Start time</label>
              <input className={inputCls} type="time" step="900" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="admin-booking-time" />
            </div>
            <div>
              <label className={labelCls}>Session duration (min)</label>
              <input className={inputCls} type="number" min="15" max="480" step="15" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="admin-booking-duration" />
            </div>
            <div>
              <label className={labelCls}>
                Buffer after (min) <span className="normal-case text-ink-soft/70">· default {defaultBuffer}</span>
              </label>
              <input
                className={inputCls}
                type="number"
                min="0"
                max="240"
                step="5"
                value={form.buffer_min}
                onChange={(e) => setForm({ ...form, buffer_min: e.target.value })}
                data-testid="admin-booking-buffer"
                placeholder="0 = none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Notes (optional)</label>
              <textarea className={`${inputCls} resize-none font-sans`} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <RepeatsField form={form} setForm={setForm} mode="session" />
          </div>
        )}

        {mode === "session" && (
          <label className="mt-5 flex items-start gap-3 p-4 rounded-xl bg-cream border border-sand/70 cursor-pointer hover:border-navy/40 transition-colors">
            <input
              type="checkbox"
              checked={form.override_schedule}
              onChange={(e) => setForm({ ...form, override_schedule: e.target.checked })}
              data-testid="admin-booking-override"
              className="mt-0.5 h-4 w-4 accent-navy"
            />
            <div className="flex-grow">
              <div className="flex items-center gap-2 text-sm text-ink font-medium">
                <ShieldAlert size={14} className="text-navy" /> Override schedule rules
              </div>
              <div className="text-xs text-ink-soft mt-0.5">
                Allow booking outside this location's working hours / on closed days / in the past. (Double-bookings are always prevented.)
              </div>
            </div>
          </label>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="admin-booking-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {mode === "block" ? "Block this time" : "Create booking"}
        </button>
      </form>
    </div>
  );
}


function RepeatsField({ form, setForm, mode }) {
  const recurring = form.recur_frequency !== "none";
  const labelCls = "text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1.5 block";
  const inputCls = "w-full bg-cream border border-sand/80 rounded-xl px-3 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy text-sm";
  return (
    <div className="sm:col-span-2 rounded-xl border border-sand/70 bg-cream-soft p-4">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCcw size={13} className="text-navy" />
        <span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Repeats</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Frequency</label>
          <select
            className={inputCls}
            value={form.recur_frequency}
            onChange={(e) => setForm({ ...form, recur_frequency: e.target.value })}
            data-testid={`${mode}-recurrence-frequency`}
          >
            <option value="none">No — single {mode === "block" ? "block" : "session"}</option>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly (every 2 weeks)</option>
          </select>
        </div>
        {recurring && (
          <div>
            <label className={labelCls}>For how many {mode === "block" ? "blocks" : "sessions"}?</label>
            <input
              type="number"
              min="2"
              max="26"
              className={inputCls}
              value={form.recur_occurrences}
              onChange={(e) => setForm({ ...form, recur_occurrences: e.target.value })}
              data-testid={`${mode}-recurrence-occurrences`}
            />
          </div>
        )}
      </div>
      {recurring && (
        <p className="text-[11px] text-ink-soft mt-3 italic">
          Any occurrence that overlaps an existing booking will be skipped automatically — the rest of the series goes ahead.
        </p>
      )}
    </div>
  );
}
